// src/components/PerformanceBanner.js
//
// Sticky, dismissible banner shown directly below the navbar advertising the
// site's live track record (overall + yesterday). Backed by the `summary`
// object attached to every /api/track/ response — see usePerformanceSummary.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePerformanceSummary } from '../hooks/usePerformanceSummary';
import './PerformanceBanner.css';

const DISMISSED_KEY = 'performanceBannerDismissedDate';

// Dismissal is scoped to the current day — "yesterday" rolls forward daily,
// so the banner content changes and is worth re-showing on the next visit.
function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function trimTrailingZero(numStr) {
  return numStr.includes('.') ? numStr.replace(/0+$/, '').replace(/\.$/, '') : numStr;
}

function formatUnits(value) {
  const num = Number(value) || 0;
  const sign = num >= 0 ? '+' : '';
  return `${sign}${trimTrailingZero(num.toFixed(2))}u`;
}

function formatPct(value) {
  const num = Number(value) || 0;
  const sign = num >= 0 ? '+' : '';
  return `${sign}${trimTrailingZero(num.toFixed(2))}%`;
}

function StatGroup({ label, stats, variant }) {
  const { wins, losses, pushes, profit_units, roi_pct } = stats;
  const profitPositive = Number(profit_units) >= 0;
  const roiPositive = Number(roi_pct) >= 0;

  return (
    <div className={`perf-banner__group perf-banner__group--${variant}`}>
      <span className="perf-banner__group-label">{label}</span>
      <div className="perf-banner__stats">
        <div className="perf-banner__stat">
          <span className="perf-banner__stat-value">
            {wins}-{losses}{pushes > 0 ? `-${pushes}` : ''}
          </span>
          <span className="perf-banner__stat-label">Record</span>
        </div>
        <div className="perf-banner__stat">
          <span className={`perf-banner__stat-value ${profitPositive ? 'is-positive' : 'is-negative'}`}>
            {formatUnits(profit_units)}
          </span>
          <span className="perf-banner__stat-label">Profit</span>
        </div>
        <div className="perf-banner__stat">
          <span className={`perf-banner__stat-value ${roiPositive ? 'is-positive' : 'is-negative'}`}>
            {formatPct(roi_pct)}
          </span>
          <span className="perf-banner__stat-label">ROI</span>
        </div>
      </div>
    </div>
  );
}

export default function PerformanceBanner() {
  const { summary, loading, error } = usePerformanceSummary();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === todayKey();
    } catch {
      return false;
    }
  });

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, todayKey());
    } catch {
      // ignore — worst case the banner re-shows next reload
    }
    setDismissed(true);
  };

  if (dismissed || loading || error || !summary) return null;

  const { overall, yesterday } = summary;
  // No settled bets yet (e.g. local/dev) — avoid showing a misleading 0-0 card.
  if (!overall || overall.bets_settled === 0) return null;

  return (
    <div className="perf-banner" role="status" aria-live="off">
      <div className="perf-banner__inner">
        <Link to="/results" className="perf-banner__link" aria-label="View full results page">
          <div className="perf-banner__title">
            <span className="perf-banner__title-icon" aria-hidden="true">📈</span>
            <span>Our Results</span>
            <span className="perf-banner__title-arrow" aria-hidden="true">›</span>
          </div>
          <div className="perf-banner__groups">
            <StatGroup label="Overall" stats={overall} variant="overall" />
            {yesterday && yesterday.bets_settled > 0 && (
              <StatGroup label="Yesterday" stats={yesterday} variant="yesterday" />
            )}
          </div>
        </Link>
        <button
          type="button"
          className="perf-banner__close"
          onClick={handleDismiss}
          aria-label="Dismiss results banner"
        >
          ×
        </button>
      </div>
    </div>
  );
}
